import { useEffect, useRef, useImperativeHandle, useMemo } from 'react';
import { useGroupKey, useJoinKeyPathToString, useNoOutlineWhenFocused, useRegisterInlineFieldEditor, useWasUndoRedoEventWithData, formatDataTokenValue, unformatNumber, getDataTokenLocale, parseFormatterAttributes } from '../lib/InlineFieldEditingHooks';
import { getLocaleSeparators } from '../lib/localeUtils';
import { setCaretPosition } from '../lib/domUtils/selectionDomUtils';
// @ts-expect-error not typed
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
// Helper function to sanitize input - handles currency symbols, percent signs, and locale-specific formatting
const sanitizeNumberInput = (input, locale, allowedCharsRegex, decimalSeparatorGlobalRegex, decimalSeparator, formatType) => {
  // If input contains symbols or is already formatted, try to unformat it first
  try {
    const unformatted = unformatNumber(input, locale);
    if (!isNaN(parseFloat(unformatted))) {
      return unformatted;
    }
  } catch (e) {
    // If unformatNumber fails, fall back to manual sanitization
  }

  // Remove any characters that aren't digits, group separator, or decimal separator
  let sanitized = input.replace(allowedCharsRegex, '');

  // Ensure only one decimal separator exists
  const decimalPoints = sanitized.match(decimalSeparatorGlobalRegex) || [];
  if (decimalPoints.length > 1) {
    const firstDecimalIndex = sanitized.indexOf(decimalSeparator);
    sanitized = sanitized.substring(0, firstDecimalIndex + 1) + sanitized.substring(firstDecimalIndex + 1).replace(decimalSeparatorGlobalRegex, '');
  }
  const hasExplicitlyDefinedFormat = formatType === 'number' || formatType === 'percent' || formatType === 'currency';
  const isEmptyValue = !sanitized;
  if (hasExplicitlyDefinedFormat && isEmptyValue) {
    return '0';
  }
  return sanitized;
};
const isValueZero = value => {
  if (value === undefined || value === null) return false;
  const numericValue = parseFloat(value.toString());
  return !isNaN(numericValue) && numericValue === 0;
};
const isValueZeroOrUndefined = value => isValueZero(value) || value === undefined;
const NumberFieldEditor = ({
  name,
  node,
  onUpdate,
  currentValue,
  keyPath,
  label,
  helpText,
  isRequired = false,
  requiredForSaving = false,
  supportsInlinePlaceholdersAndLabels,
  moduleName,
  onFocusCallback,
  onBlurCallback,
  registerInlineFieldRef
}) => {
  const textRef = useRef(null);
  useNoOutlineWhenFocused(node);
  useRegisterInlineFieldEditor(moduleName);
  const fieldKeyString = useJoinKeyPathToString(keyPath);
  const groupKey = useGroupKey(moduleName, fieldKeyString, name);

  // Memoize locale and separators since they don't change during component lifecycle
  const locale = useMemo(() => getDataTokenLocale(node), [node]);
  const formatterAttributes = useMemo(() => parseFormatterAttributes(node), [node]);
  const formatType = formatterAttributes === null || formatterAttributes === void 0 ? void 0 : formatterAttributes.formatType;
  const zeroPlaceholder = useMemo(() => node.getAttribute('hs-data-token-zero-placeholder'), [node]);
  const {
    groupSeparator,
    decimalSeparator
  } = useMemo(() => getLocaleSeparators(locale), [locale]);

  // Memoize regex patterns to avoid recreating them on every input event
  const regexPatterns = useMemo(() => {
    const escapeRegex = /[.*+?^${}()|[\]\\]/g;
    const groupSeparatorEscaped = groupSeparator.replace(escapeRegex, '\\$&');
    const decimalSeparatorEscaped = decimalSeparator.replace(escapeRegex, '\\$&');
    return {
      allowedChars: new RegExp(`[^\\d${groupSeparatorEscaped}${decimalSeparatorEscaped}]`, 'g'),
      decimalSeparatorGlobal: new RegExp(decimalSeparatorEscaped, 'g'),
      numericAndDecimal: new RegExp(`[\\d${decimalSeparatorEscaped}]`, 'g'),
      digitTest: /\d/,
      numericKey: /^[0-9]$/,
      decimalSeparatorEscaped
    };
  }, [groupSeparator, decimalSeparator]);
  const wasUndoRedo = useWasUndoRedoEventWithData(currentValue);
  useImperativeHandle(textRef, () => {
    return {
      focus: () => {
        if (document.activeElement !== node) {
          node.focus();
          // We don't have click coordinates from the inline toolbar or sidebar
          // so default to manually clicking the top right of the field
          const {
            right,
            top
          } = node.getBoundingClientRect();
          setCaretPosition(right - 5, top + 5);
          onFocusCallback();
        }
      }
    };
  }, [node, onFocusCallback]);

  // @TODO: This may fall apart once we have modules with inline & non-inline fields
  // that experience a live update via Remy (because the ref depends on the module DOM node)
  useEffect(() => {
    registerInlineFieldRef === null || registerInlineFieldRef === void 0 || registerInlineFieldRef(name, 'number', textRef.current);
  }, [registerInlineFieldRef, name]);
  useEffect(() => {
    if (!supportsInlinePlaceholdersAndLabels) {
      return;
    }
    const inpageConfig = EditorConfigSingleton.get('inpage');
    if (inpageConfig && inpageConfig.shouldUseInlinePlaceholdersAndLabels) {
      // Setting data attributes so our css can render psuedo placeholders with the text on
      // the attributes when the field is empty
      if (helpText) {
        node.setAttribute('data-hs-inline-help-text', helpText);
      }
      if (label) {
        node.setAttribute('data-hs-inline-label', label);
        if (isRequired) {
          // Note: setting a value of '*' instead of `true` because the pseudo elements render the raw string
          // that lives on the data attribute
          node.setAttribute('data-hs-inline-label-required', '*');
        }
      }
    }
  }, [helpText, isRequired, label, node, supportsInlinePlaceholdersAndLabels]);

  // Set content editble on provided node + change listeners on them
  useEffect(() => {
    let lastValue = '';
    const updateListener = e => {
      const target = e.target;
      let updatedText = target.innerText;

      // Sanitize the input to only allow numbers and decimal point
      const sanitizedText = sanitizeNumberInput(updatedText, locale, regexPatterns.allowedChars, regexPatterns.decimalSeparatorGlobal, decimalSeparator, formatType);

      // Check if content was deleted (comparing sanitized lengths)
      const wasDeleting = sanitizedText.length < sanitizeNumberInput(lastValue, locale, regexPatterns.allowedChars, regexPatterns.decimalSeparatorGlobal, decimalSeparator, formatType).length;
      lastValue = updatedText;

      // If we're deleting and the sanitized text is empty, don't format
      if (wasDeleting && sanitizedText === '') {
        if (requiredForSaving && sanitizedText === '') {
          return;
        }
        // Save the raw numeric value (already sanitized, so no formatting to strip)
        onUpdate(keyPath, sanitizedText, {
          groupKey
        });
        return;
      }

      // Apply formatting to the sanitized text
      const formattedText = formatDataTokenValue(target, sanitizedText);

      // Update the DOM node with the formatted text if it's different
      if (formattedText !== updatedText) {
        // Save current cursor position and extract numeric content before cursor
        const selection = window.getSelection();
        let numericContentBeforeCursor = '';
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const textBeforeCursor = updatedText.substring(0, range.startOffset);
          // Extract only numeric characters and decimal separator from text before cursor
          numericContentBeforeCursor = (textBeforeCursor.match(regexPatterns.numericAndDecimal) || []).join('');
          // When a user attempts to replace the first zero character in a numeric field,
          // numericContentBeforeCursor will ultimately be formatted to remove the
          // leading zero, but not before this point. We should therefore not consider
          // a leading zero to be part of the numeric content before the cursor.
          numericContentBeforeCursor = numericContentBeforeCursor.replace(/^0+/, '');
        }
        target.innerText = formattedText;

        // Calculate new cursor position by matching numeric content
        if (selection && numericContentBeforeCursor !== undefined) {
          let newCursorPosition = 0;
          let matchedNumericContent = '';
          let firstNumericCharacterIndex = undefined;
          const hasNoNumericContentBeforeCursor = numericContentBeforeCursor === '';
          // Iterate through formatted text until we match all the numeric content before cursor
          for (let i = 0; i < formattedText.length; i++) {
            const char = formattedText[i];
            const isNumericOrDecimal = regexPatterns.digitTest.test(char) || char === decimalSeparator;
            if (isNumericOrDecimal) {
              matchedNumericContent += char;
              if (hasNoNumericContentBeforeCursor) {
                firstNumericCharacterIndex = i + 1;
                break;
              }

              // If we've matched all the numeric content that was before cursor,
              // place cursor after this character
              if (matchedNumericContent === numericContentBeforeCursor) {
                newCursorPosition = i + 1;
                break;
              }
            }
          }
          if (hasNoNumericContentBeforeCursor && firstNumericCharacterIndex) {
            // If the user has deleted the first character, put the character after the first numeric
            // character in the newly formatted text.
            newCursorPosition = firstNumericCharacterIndex;
          } else if (matchedNumericContent !== numericContentBeforeCursor) {
            // If we couldn't match (shouldn't happen), default to end
            newCursorPosition = formattedText.length;
          }

          // Set cursor position
          const newRange = document.createRange();
          const textNode = target.firstChild;
          if (textNode) {
            newRange.setStart(textNode, Math.min(newCursorPosition, formattedText.length));
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
        updatedText = formattedText;
      }
      if (requiredForSaving && updatedText === '') {
        return;
      }
      onUpdate(keyPath, sanitizedText, {
        groupKey
      });
    };
    const onPaste = e => {
      if (e.clipboardData) {
        e.preventDefault();

        // Get pasted content and sanitize it
        const paste = e.clipboardData.getData('text/plain');
        const sanitizedPaste = sanitizeNumberInput(paste, locale, regexPatterns.allowedChars, regexPatterns.decimalSeparatorGlobal, decimalSeparator, formatType);

        // Apply formatting to the sanitized paste
        const formattedPaste = formatDataTokenValue(node, sanitizedPaste);
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) {
          return;
        }
        selection.deleteFromDocument();
        selection.getRangeAt(0).insertNode(document.createTextNode(formattedPaste));
        selection.collapseToEnd();
        node.normalize();

        // Save the sanitized value (not the formatted one) to the data model
        onUpdate(keyPath, sanitizedPaste, {
          groupKey
        });
      }
    };
    const onKeyDown = e => {
      // Allow: special keys, navigation keys, backspace, delete, tab, escape
      const specialKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

      // Allow ctrl/cmd+a, ctrl/cmd+c, ctrl/cmd+v, ctrl/cmd+x
      const ctrlCombos = ['a', 'c', 'v', 'x'];
      if (specialKeys.includes(e.key) || e.ctrlKey && ctrlCombos.includes(e.key.toLowerCase()) || e.metaKey && ctrlCombos.includes(e.key.toLowerCase())) {
        // Let the event proceed
        return;
      }

      // Allow numeric keys, decimal separator, and group separator for this locale
      const isNumericKey = regexPatterns.numericKey.test(e.key);
      const isDecimalSeparator = e.key === decimalSeparator;
      const isGroupSeparator = e.key === groupSeparator;
      if (isNumericKey || isDecimalSeparator || isGroupSeparator) {
        // If it's a decimal separator, check if one already exists
        if (isDecimalSeparator && node.innerText.includes(decimalSeparator)) {
          e.preventDefault();

          // Move cursor to just after the existing decimal separator
          const text = node.innerText;
          const decimalIndex = text.indexOf(decimalSeparator);
          if (decimalIndex !== -1) {
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              const textNode = node.firstChild;
              if (textNode) {
                // Position cursor right after the decimal separator
                range.setStart(textNode, decimalIndex + 1);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
              }
            }
          }
          return;
        }
        // Else let the event proceed
        return;
      }

      // Prevent any other key
      e.preventDefault();
    };
    node.setAttribute('contenteditable', 'true');
    node.addEventListener('input', updateListener);
    node.addEventListener('paste', onPaste);
    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('input', updateListener);
      node.removeEventListener('keydown', onKeyDown);
      node.removeEventListener('paste', onPaste);
      node.removeAttribute('contenteditable');
    };
  }, [node, onUpdate, keyPath, groupKey, requiredForSaving, locale, groupSeparator, decimalSeparator, regexPatterns, formatType]);

  // The currentValue dependency required for onFocus and onBlur causes the listeners
  // in the above useEffect to behave less smoothly, so it's isolated here.
  useEffect(() => {
    let textBeforeEditing = null;
    const onFocus = () => {
      textBeforeEditing = node.innerText;
      const textBeforeFocus = node.innerText;

      // If the field is required for saving, check to see if the current value
      // is equal to the translated help text. If it is, assume that the value is
      // placeholder content and clear it from the DOM on node focus.
      if (requiredForSaving && helpText && textBeforeFocus === helpText) {
        node.innerText = '';
      } else if (zeroPlaceholder && textBeforeFocus === zeroPlaceholder && isValueZeroOrUndefined(currentValue)) {
        node.innerText = formatDataTokenValue(node, '0');
      }
      onFocusCallback();
    };
    const onBlur = () => {
      // If the field is required for saving and they're attempting to save an empty
      // string, replace the empty string with the content they had prior to making
      // any updates and save that old value again
      if (requiredForSaving && node.innerText === '' && textBeforeEditing) {
        node.innerText = textBeforeEditing;
        onUpdate(keyPath, textBeforeEditing, {
          groupKey
        });
      } else if (zeroPlaceholder && isValueZeroOrUndefined(currentValue)) {
        node.innerText = zeroPlaceholder;
      }
      textBeforeEditing = null;
      onBlurCallback();
    };
    node.addEventListener('focus', onFocus);
    node.addEventListener('blur', onBlur);
    return () => {
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onBlur);
    };
  }, [node, zeroPlaceholder, currentValue, requiredForSaving, helpText, onFocusCallback, onBlurCallback, groupKey, keyPath, onUpdate]);

  // Make sure updates from the sidebar are also applied to the DOM
  useEffect(() => {
    if (currentValue !== undefined && currentValue !== null && (
    // Always apply the `currentValue` if the change came from an undo/redo action
    document.activeElement !== node || wasUndoRedo)) {
      const numericValue = parseFloat(currentValue.toString());
      if (!isNaN(numericValue)) {
        const formattedValue = isValueZero(currentValue) && zeroPlaceholder ? zeroPlaceholder : formatDataTokenValue(node, currentValue);

        // Only update if the formatted value is different from what's currently displayed
        if (node.innerText !== formattedValue) {
          node.innerText = formattedValue;
        }
      }
    }
  }, [currentValue, node, wasUndoRedo, groupSeparator, decimalSeparator, locale, zeroPlaceholder]);
  return null;
};
export default NumberFieldEditor;